/**
 * Shared x-axis tick row for `StackedBars` and `Line` — Field Notes
 * chart set.
 *
 * It exists because both charts had NO x axis at all, and that is what
 * let a silently dropped bucket look like a shorter but continuous
 * series: with only per-point `<title>` tooltips, "6 bars" and "7 bars"
 * read the same (#20, #46). The tick row is also the cheapest possible
 * proof that the bucket count is what the window says — one cell per
 * bucket, always, even when the cell carries no text.
 *
 * It is HTML, deliberately not SVG. Both charts draw with
 * `preserveAspectRatio="none"`, so an SVG `<text>` inside them would be
 * stretched horizontally by whatever ratio the container happens to
 * have. The row instead reproduces the charts' geometry in percentages
 * of the same container width, which is exact because the viewBox is
 * stretched to that same width:
 *
 *   - `bars`   — mirrors `StackedBars` (viewBox 960, padding 16, gap 6):
 *                one flex cell per bucket, `flex: 1 1 0`, so cell N sits
 *                exactly under bar N.
 *   - `points` — mirrors `Line` (viewBox 640, padding 12): zero-width
 *                cells spread with `space-between`, so cell N sits
 *                exactly under point N.
 *
 * Label text is absolutely centered inside its cell rather than laid
 * out in it: at 90 buckets a cell is ~4px wide and in-flow text would
 * be clipped to nothing. Only a SAMPLE of cells carries text (first and
 * last always); the rest stay empty but present.
 */

const BARS_VIEW_WIDTH = 960;
const BARS_PADDING = 16;
const BARS_GAP = 6;
const POINTS_VIEW_WIDTH = 640;
const POINTS_PADDING = 12;

/** How many ticks may carry text before they start colliding. */
const MAX_TEXT_TICKS = 8;

export type ChartXAxisLayout = 'bars' | 'points';

interface Props {
  /** One label per bucket/point, aligned by index with the chart's data. */
  readonly labels: readonly string[];
  readonly layout: ChartXAxisLayout;
  readonly testId?: string;
}

/**
 * `2026-08-01` → `08-01`. Week (`2026-W31`) and month (`2026-08`) keys
 * are already short and pass through untouched.
 */
function shortenTick(label: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(label) ? label.slice(5) : label;
}

/**
 * Indices allowed to render text: always the first and the last, the
 * rest spread evenly. Returns every index while the count still fits.
 */
export function sampledTickIndices(count: number, max = MAX_TEXT_TICKS): ReadonlySet<number> {
  if (count <= 0) return new Set();
  if (count <= max) return new Set(Array.from({ length: count }, (_, index) => index));
  const step = (count - 1) / (max - 1);
  return new Set(Array.from({ length: max }, (_, index) => Math.round(index * step)));
}

export function ChartXAxis({ labels, layout, testId }: Props): JSX.Element | null {
  if (labels.length === 0) return null;
  const bars = layout === 'bars';
  const viewWidth = bars ? BARS_VIEW_WIDTH : POINTS_VIEW_WIDTH;
  const padding = bars ? BARS_PADDING : POINTS_PADDING;
  const textAt = sampledTickIndices(labels.length);
  // `h-4`: every cell's text is absolutely positioned, so the row would
  // otherwise collapse to zero and overlap whatever sits below it.
  return (
    <div
      data-testid={testId}
      className="flex h-4 w-full items-start text-mono-label text-ink-low"
      style={{
        paddingInline: `${(padding / viewWidth) * 100}%`,
        columnGap: bars ? `${(BARS_GAP / viewWidth) * 100}%` : 0,
        // A single point is centered by `Line`, not spread to the edges.
        justifyContent: bars ? 'flex-start' : labels.length > 1 ? 'space-between' : 'center',
      }}
    >
      {labels.map((label, index) => (
        <span
          key={`${index}-${label}`}
          data-testid={testId === undefined ? undefined : `${testId}-cell`}
          className="relative block"
          style={{ flex: bars ? '1 1 0' : '0 0 0', minWidth: 0 }}
        >
          {textAt.has(index) ? (
            <span className="absolute left-1/2 top-0 -translate-x-1/2 whitespace-nowrap font-mono">
              {shortenTick(label)}
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}
